import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSession } from '../contexts/SessionContext';
import {
  LayoutDashboard, Users, GraduationCap, BookOpen,
  DollarSign, ClipboardCheck, FileText, Calendar, Bell,
  LogOut, School, Settings, Sparkles, ChevronDown,
  PanelLeftClose, PanelLeft, Search, X,
} from 'lucide-react';
import logo from '../assets/LuminaAI.png';
import { SpotlightSearch } from './SpotlightSearch';

/* ─── Constants ──────────────────────────────────────────────────────────── */
const SB_EXPANDED  = 220;   // sidebar expanded width px
const SB_COLLAPSED = 64;    // sidebar collapsed (icon-only) width px
const SB_GAP       = 8;    // gap from all floating edges
const TB_H         = 62;    // top bar height px

/* ─── Inject global CSS once ─────────────────────────────────────────────── */
const STYLE = `
  .sb-no-scroll { scrollbar-width:none; -ms-overflow-style:none; }
  .sb-no-scroll::-webkit-scrollbar { display:none; }
`;

/* ─── Hover tooltip (collapsed sidebar) ─────────────────────────────────── */
function Tooltip({ label }) {
  return (
    <span style={{
      position:'absolute', left:'calc(100% + 12px)', top:'50%',
      transform:'translateY(-50%)', zIndex:9999,
      background:'rgba(15,23,42,0.92)', color:'#f1f5f9',
      fontSize:11, fontWeight:600, padding:'5px 10px', borderRadius:8,
      whiteSpace:'nowrap', pointerEvents:'none',
      border:'1px solid rgba(255,255,255,0.08)',
      boxShadow:'0 4px 16px rgba(0,0,0,0.2)',
    }}>
      <span style={{
        position:'absolute', right:'100%', top:'50%', transform:'translateY(-50%)',
        borderTop:'5px solid transparent', borderBottom:'5px solid transparent',
        borderRight:'5px solid rgba(15,23,42,0.92)',
      }}/>
      {label}
    </span>
  );
}

/* ─── Single nav item ────────────────────────────────────────────────────── */
function NavItem({ item, collapsed, active, onClick }) {
  const [hov, setHov] = useState(false);
  const Icon = item.icon;
  return (
    <div style={{ position:'relative' }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <Link
        to={item.path}
        onClick={onClick}
        style={{
          display:'flex', alignItems:'center',
          gap:10, padding:'7px 10px',
          borderRadius:12, textDecoration:'none', minHeight:40,
          background: active ? `${item.color}14` : hov ? '#f8fafc' : 'transparent',
          border:`1px solid ${active ? `${item.color}28` : 'transparent'}`,
          transition:'all 0.14s ease',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        {/* Icon box */}
        <div style={{
          width:30, height:30, borderRadius:9, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'center',
          background: active ? `${item.color}18` : 'transparent',
        }}>
          <Icon size={15} style={{ color: active ? item.color : hov ? '#475569' : '#94a3b8', transition:'color 0.14s' }}/>
        </div>

        {/* Label (expanded only) */}
        {!collapsed && (
          <span style={{
            fontSize:12.5, fontWeight: active ? 600 : 500,
            color: active ? '#1e293b' : '#64748b',
            flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
          }}>
            {item.label}
          </span>
        )}

        {/* Active dot */}
        {active && !collapsed && (
          <span style={{ width:5, height:5, borderRadius:'50%', background:item.color, flexShrink:0 }}/>
        )}
      </Link>

      {/* Tooltip — collapsed mode only */}
      {collapsed && hov && <Tooltip label={item.label}/>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Layout
═══════════════════════════════════════════════════════════════════════════ */
export const Layout = ({ children }) => {
  const { user, logout }                             = useAuth();
  const { selectedSession, sessions, updateSession } = useSession();
  const navigate  = useNavigate();
  const location  = useLocation();

  /* Sidebar: 'expanded' | 'collapsed' */
  const [sbMode, setSbMode] = useState(
    () => localStorage.getItem('sb_mode2') || 'expanded'
  );
  const expanded = sbMode === 'expanded';
  const sbWidth  = expanded ? SB_EXPANDED : SB_COLLAPSED;

  const toggleSb = () => {
    const next = expanded ? 'collapsed' : 'expanded';
    setSbMode(next);
    localStorage.setItem('sb_mode2', next);
  };

  /* Mobile overlay */
  const [mobileOpen, setMobileOpen] = useState(false);

  /* Spotlight search */
  const [spotOpen, setSpotOpen] = useState(false);

  /* User dropdown */
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef(null);

  /* Desktop breakpoint — for content margin */
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 1024
  );

  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= 1024);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  /* Close user dropdown on outside click */
  useEffect(() => {
    const fn = (e) => {
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  /* Close mobile drawer on route change */
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  /* ── Menu definitions ─────────────────────────────────────────── */
  const adminMenuItems = [
    { icon:LayoutDashboard, label:'Dashboard',     path:'/dashboard',          color:'#6366f1' },
    { icon:School,          label:'Classes',        path:'/admin/classes',       color:'#8b5cf6' },
    { icon:Users,           label:'Students',       path:'/admin/students',      color:'#3b82f6' },
    { icon:GraduationCap,   label:'Teachers',       path:'/admin/teachers',      color:'#06b6d4' },
    { icon:Users,           label:'Class Teachers', path:'/admin/class-teacher', color:'#0ea5e9' },
    { icon:BookOpen,        label:'Subjects',       path:'/admin/subjects',      color:'#10b981' },
    { icon:DollarSign,      label:'Fees',           path:'/admin/fees',          color:'#f59e0b' },
    { icon:ClipboardCheck,  label:'Attendance',     path:'/admin/attendance',    color:'#ef4444' },
    { icon:FileText,        label:'Exams',          path:'/admin/exams',         color:'#f97316' },
    { icon:Calendar,        label:'Homework',       path:'/admin/homework',      color:'#84cc16' },
    { icon:Bell,            label:'Announcements',  path:'/admin/announcements', color:'#ec4899' },
    { icon:Settings,        label:'Settings',       path:'/admin/settings',      color:'#64748b' },
  ];
  const teacherMenuItems = [
    { icon:LayoutDashboard, label:'Dashboard',     path:'/dashboard',             color:'#6366f1' },
    { icon:ClipboardCheck,  label:'Attendance',    path:'/teacher/attendance',    color:'#ef4444' },
    { icon:FileText,        label:'Marks Entry',   path:'/teacher/marks',         color:'#f97316' },
    { icon:Calendar,        label:'Homework',      path:'/teacher/homework',      color:'#84cc16' },
    { icon:Bell,            label:'Announcements', path:'/teacher/announcements', color:'#ec4899' },
  ];
  const studentMenuItems = [
    { icon:Bell, label:'Announcements', path:'/student/announcements', color:'#ec4899' },
  ];
  const superAdminMenuItems = [
    { icon:School,          label:'Onboarding', path:'/internal/onboarding', color:'#8b5cf6' },
    { icon:LayoutDashboard, label:'Overview',   path:'/dashboard',           color:'#6366f1' },
  ];

  const moduleMapping = {
    'Classes':'classes','Students':'students','Teachers':'teachers',
    'Class Teachers':'classTeachers','Subjects':'subjects','Fees':'fees',
    'Attendance':'attendance','Exams':'exams','Marks Entry':'exams',
    'Homework':'homework','Announcements':'announcements','BirdAI':'birdAI',
  };
  const enabledModules = user?.school?.enabledModules || {
    classes:true,students:true,teachers:true,classTeachers:true,
    subjects:true,fees:true,attendance:true,exams:true,
    homework:true,announcements:true,birdAI:true,
  };
  const isModuleEnabled = (label) => {
    const k = moduleMapping[label];
    return !k || enabledModules[k] !== false;
  };
  const isCurrentPathEnabled = () => {
    const p = location.pathname;
    if (p.includes('/classes')       && enabledModules.classes       === false) return false;
    if (p.includes('/students')      && enabledModules.students      === false) return false;
    if (p.includes('/teachers')      && enabledModules.teachers      === false) return false;
    if (p.includes('/class-teacher') && enabledModules.classTeachers === false) return false;
    if (p.includes('/subjects')      && enabledModules.subjects      === false) return false;
    if (p.includes('/fees')          && enabledModules.fees          === false) return false;
    if (p.includes('/attendance')    && enabledModules.attendance    === false) return false;
    if ((p.includes('/exams') || p.includes('/marks')) && enabledModules.exams === false) return false;
    if (p.includes('/homework')      && enabledModules.homework      === false) return false;
    if (p.includes('/announcements') && enabledModules.announcements === false) return false;
    if (p.includes('/bird-ai')       && enabledModules.birdAI        === false) return false;
    return true;
  };

  const rawMenu =
    user?.role === 'superadmin' ? superAdminMenuItems :
    user?.role === 'admin'      ? adminMenuItems       :
    user?.role === 'teacher'    ? teacherMenuItems     :
    studentMenuItems;

  const filteredMenu = rawMenu.filter(i => isModuleEnabled(i.label));
  const isActive     = (path) => location.pathname === path;
  const currentPage  = filteredMenu.find(i => isActive(i.path));

  const handleLogout = () => { logout(); navigate('/'); };

  /* Disabled module message */
  const renderDisabled = () => (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', textAlign:'center', padding:24 }}>
      <div style={{ background:'white', border:'1px solid #f1f5f9', borderRadius:24, padding:40, maxWidth:400, boxShadow:'0 2px 20px rgba(0,0,0,0.06)' }}>
        <div style={{ width:64, height:64, borderRadius:'50%', background:'#fef2f2', border:'1px solid #fee2e2', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
          <X size={28} style={{ color:'#ef4444' }}/>
        </div>
        <h2 style={{ fontSize:20, fontWeight:700, color:'#0f172a', margin:'0 0 8px' }}>Module Disabled</h2>
        <p style={{ fontSize:14, color:'#64748b', margin:'0 0 24px', lineHeight:1.6 }}>This module has been disabled by the administrator.</p>
        <button onClick={() => navigate('/dashboard')}
          style={{ background:'#6366f1', color:'white', border:'none', borderRadius:50, padding:'10px 28px', fontSize:14, fontWeight:600, cursor:'pointer' }}>
          Return to Dashboard
        </button>
      </div>
    </div>
  );

  /* Content left offset = sidebar right edge + inner gap */
  const contentMarginLeft = isDesktop ? sbWidth + SB_GAP * 2 : 0;

  /* ─────────────────────────────────────────────────────────────────
     RENDER
  ───────────────────────────────────────────────────────────────── */
  return (
    <div style={{ minHeight:'100vh', background:'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 50%, #f5f3ff 100%)' }}>
      <style>{STYLE}</style>

      {/* ══════════════════════ TOP BAR ══════════════════════════════ */}
      <header style={{
        position:'fixed',
        top:SB_GAP,
        left:SB_GAP,
        right:SB_GAP,
        height:TB_H,
        background:'rgba(255, 255, 255, 0.65)',
        backdropFilter:'blur(16px)',
        WebkitBackdropFilter:'blur(16px)',
        borderRadius:16,
        border:'1px solid rgba(255, 255, 255, 0.4)',
        display:'flex', alignItems:'center', gap:12, padding:'0 10px',
        zIndex:400,
        boxShadow:'0 10px 30px -10px rgba(99, 102, 241, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)',
      }}>

        {/* Brand + current page */}
        <div style={{ display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
          <div style={{ width:50, height:50, borderRadius:9, background:'#eef2ff', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
            <img src={logo} alt="Logo" style={{ width:50, height:50, objectFit:'cover' }}/>
          </div>
          <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Lumina AI Campus</span>
          {currentPage && (
            <>
              <span style={{ color:'#cbd5e1', fontSize:20, fontWeight:200, lineHeight:1 }}>|</span>
              <span style={{ fontSize:13, fontWeight:500, color:'#64748b' }}>{currentPage.label}</span>
            </>
          )}
        </div>

        {/* Search — center */}
        <div style={{ flex:1, maxWidth:380, margin:'0 auto' }}>
          <button
            onClick={() => setSpotOpen(true)}
            style={{
              width:'100%', display:'flex', alignItems:'center', gap:8,
              padding:'8px 16px', borderRadius:24, cursor:'pointer',
              background:'#f1f5f9', border:'1px solid #f1f5f9',
              color:'#94a3b8', fontSize:13, transition:'all 0.14s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#e8edf4'; }}
            onMouseLeave={e => { e.currentTarget.style.background='#f1f5f9'; }}
          >
            <Search size={13} style={{ flexShrink:0 }}/>
            <span style={{ flex:1, textAlign:'left' }}>What are you looking for?</span>
            <span style={{ fontSize:10, fontWeight:700, color:'#cbd5e1', background:'white', border:'1px solid #e8edf4', padding:'1px 7px', borderRadius:6 }}>⌘K</span>
          </button>
        </div>

        {/* Right controls */}
        <div style={{ display:'flex', alignItems:'center', gap:8, marginLeft:'auto', flexShrink:0 }}>

          {/* Session picker */}
          <select
            value={selectedSession || 'all'}
            onChange={e => updateSession(e.target.value)}
            style={{ fontSize:12, fontWeight:600, color:'#475569', background:'#f8fafc', border:'1px solid #e8edf4', borderRadius:10, padding:'6px 10px', outline:'none', cursor:'pointer' }}
          >
            <option value="all">All Sessions</option>
            {sessions.map(s => <option key={s.id} value={s.sessionYear}>{s.sessionYear}</option>)}
          </select>

          {/* Settings */}
          <button
            onClick={() => navigate('/admin/settings')}
            title="Settings"
            style={{
              width:36, height:36, borderRadius:10,
              background:'#f8fafc', border:'1px solid #f1f5f9',
              display:'flex', alignItems:'center', justifyContent:'center',
              cursor:'pointer', transition:'background 0.14s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#f1f5f9'; }}
            onMouseLeave={e => { e.currentTarget.style.background='#f8fafc'; }}
          >
            <Settings size={14} style={{ color:'#64748b' }}/>
          </button>

          {/* Notifications bell */}
          <button style={{
            position:'relative', width:36, height:36, borderRadius:10,
            background:'#f8fafc', border:'1px solid #f1f5f9',
            display:'flex', alignItems:'center', justifyContent:'center',
            cursor:'pointer', transition:'background 0.14s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background='#f1f5f9'; }}
          onMouseLeave={e => { e.currentTarget.style.background='#f8fafc'; }}>
            <Bell size={14} style={{ color:'#64748b' }}/>
            <span style={{ position:'absolute', top:7, right:7, width:7, height:7, borderRadius:'50%', background:'#ef4444', border:'1.5px solid white' }}/>
          </button>

          {/* User menu */}
          <div ref={userRef} style={{ position:'relative' }}>
            <button
              onClick={() => setUserOpen(v => !v)}
              style={{
                display:'flex', alignItems:'center', gap:8,
                padding:'4px 12px 4px 4px', borderRadius:24,
                background:'#f8fafc', border:'1px solid #f1f5f9',
                cursor:'pointer', transition:'background 0.14s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background='#f1f5f9'; }}
              onMouseLeave={e => { e.currentTarget.style.background='#f8fafc'; }}
            >
              <div style={{
                width:28, height:28, borderRadius:'50%',
                background:'linear-gradient(135deg,#6366f1,#8b5cf6)',
                display:'flex', alignItems:'center', justifyContent:'center',
                fontSize:11, fontWeight:800, color:'white', flexShrink:0,
              }}>
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div style={{ textAlign:'left', lineHeight:1.3 }}>
                <p style={{ fontSize:12, fontWeight:700, color:'#0f172a', margin:0 }}>{user?.name || 'User'}</p>
                <p style={{ fontSize:10, color:'#94a3b8', margin:0, textTransform:'capitalize' }}>{user?.role}</p>
              </div>
              <ChevronDown size={12} style={{ color:'#94a3b8' }}/>
            </button>

            {userOpen && (
              <div style={{
                position:'absolute', right:0, top:'calc(100% + 8px)',
                background:'white', border:'1px solid #f1f5f9',
                borderRadius:16, boxShadow:'0 8px 32px rgba(0,0,0,0.10)',
                minWidth:168, padding:6, zIndex:500,
              }}>
                <div style={{ padding:'10px 12px', borderBottom:'1px solid #f8fafc', marginBottom:4 }}>
                  <p style={{ fontSize:12, fontWeight:700, color:'#0f172a', margin:0 }}>{user?.name}</p>
                  <p style={{ fontSize:11, color:'#94a3b8', margin:'2px 0 0' }}>{user?.email || user?.schoolCode}</p>
                </div>
                <button
                  onClick={handleLogout}
                  style={{
                    width:'100%', display:'flex', alignItems:'center', gap:8,
                    padding:'8px 10px', borderRadius:10, cursor:'pointer',
                    background:'transparent', border:'none',
                    color:'#ef4444', fontSize:12, fontWeight:600, transition:'background 0.14s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background='#fef2f2'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='transparent'; }}
                >
                  <LogOut size={13}/>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ══════════════ DESKTOP FLOATING SIDEBAR ═════════════════════ */}
      <aside
        className="hidden lg:flex"
        style={{
          position:'fixed',
          left:SB_GAP, top:SB_GAP + TB_H + SB_GAP, bottom:SB_GAP,
          width:sbWidth,
          borderRadius:18,
          background:'rgba(255, 255, 255, 0.65)',
          backdropFilter:'blur(16px)',
          WebkitBackdropFilter:'blur(16px)',
          border:'1px solid rgba(255, 255, 255, 0.4)',
          boxShadow:'0 10px 40px -10px rgba(99, 102, 241, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5)',
          transition:'width 0.28s cubic-bezier(0.4,0,0.2,1)',
          overflow:'hidden',
          flexDirection:'column',
          zIndex:300,
        }}
      >
        {/* Section label */}
        {/* <div style={{
          padding: expanded ? '11px 14px 5px' : '11px 0 5px',
          flexShrink:0, overflow:'hidden',
        }}>
          {expanded
            ? <span style={{ fontSize:9, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.12em' }}>Apps</span>
            : <span style={{ display:'block', width:'60%', height:1, background:'#f1f5f9', margin:'4px auto 0' }}/>
          }
        </div> */}

        {/* Nav items */}
        <nav className="sb-no-scroll" style={{ flex:1, overflowY:'auto', padding:'4px 8px', display:'flex', flexDirection:'column', gap:2 }}>
          {filteredMenu.map(item => (
            <NavItem key={item.path} item={item} collapsed={!expanded} active={isActive(item.path)}/>
          ))}
        </nav>

        {/* Footer: BirdAI + collapse toggle */}
        <div style={{ padding:8, borderTop:'1px solid #f8fafc', flexShrink:0, display:'flex', flexDirection:'column', gap:2 }}>

          <NavItem
            item={{ icon:Sparkles, label:'BirdAI', path:'/admin/bird-ai', color:'#7c3aed' }}
            collapsed={!expanded}
            active={isActive('/admin/bird-ai')}
          />

          {/* Toggle expand / collapse */}
          <button
            onClick={toggleSb}
            style={{
              display:'flex', alignItems:'center', gap:10,
              padding:'7px 10px', borderRadius:12, cursor:'pointer',
              background:'transparent', border:'1px solid transparent',
              transition:'all 0.14s ease', width:'100%', minHeight:40,
              justifyContent: expanded ? 'flex-start' : 'center',
            }}
            onMouseEnter={e => { e.currentTarget.style.background='#f8fafc'; e.currentTarget.style.borderColor='#f1f5f9'; }}
            onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='transparent'; }}
          >
            <div style={{ width:30, height:30, borderRadius:9, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              {expanded
                ? <PanelLeftClose size={14} style={{ color:'#94a3b8' }}/>
                : <PanelLeft     size={14} style={{ color:'#94a3b8' }}/>
              }
            </div>
            {expanded && <span style={{ fontSize:12.5, fontWeight:500, color:'#94a3b8' }}>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* ══════════════ MOBILE SIDEBAR ════════════════════════════════ */}
      {mobileOpen && (
        <div
          className="lg:hidden"
          onClick={() => setMobileOpen(false)}
          style={{
            position:'fixed', inset:0, zIndex:350,
            background:'rgba(15,23,42,0.25)', backdropFilter:'blur(4px)',
          }}
        />
      )}
      <aside
        className="lg:hidden"
        style={{
          position:'fixed', top:0, left:0, height:'100%', width:260,
          zIndex:360, background:'white',
          boxShadow:'8px 0 40px rgba(0,0,0,0.12)',
          transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition:'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          display:'flex', flexDirection:'column',
        }}
      >
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 14px 14px', borderBottom:'1px solid #f8fafc' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <img src={logo} alt="Logo" style={{ width:26, height:26, objectFit:'contain' }}/>
            <span style={{ fontSize:14, fontWeight:700, color:'#0f172a' }}>Lumina AI Campus</span>
          </div>
          <button onClick={() => setMobileOpen(false)} style={{ padding:6, borderRadius:9, border:'none', background:'#f8fafc', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <X size={15} style={{ color:'#94a3b8' }}/>
          </button>
        </div>

        <div style={{ padding:'10px 14px 4px' }}>
          <span style={{ fontSize:9, fontWeight:800, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.12em' }}>Apps</span>
        </div>

        <nav className="sb-no-scroll" style={{ flex:1, overflowY:'auto', padding:'4px 8px', display:'flex', flexDirection:'column', gap:2 }}>
          {filteredMenu.map(item => (
            <NavItem key={item.path} item={item} collapsed={false} active={isActive(item.path)} onClick={() => setMobileOpen(false)}/>
          ))}
        </nav>

        <div style={{ padding:8, borderTop:'1px solid #f8fafc' }}>
          <button onClick={handleLogout} style={{
            width:'100%', display:'flex', alignItems:'center', gap:10,
            padding:'8px 10px', borderRadius:12, cursor:'pointer',
            background:'transparent', border:'1px solid transparent', transition:'all 0.14s ease',
          }}
          onMouseEnter={e => { e.currentTarget.style.background='#fef2f2'; e.currentTarget.style.borderColor='#fee2e2'; }}
          onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.borderColor='transparent'; }}>
            <div style={{ width:30, height:30, borderRadius:9, background:'#fef2f2', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <LogOut size={13} style={{ color:'#ef4444' }}/>
            </div>
            <span style={{ fontSize:12.5, fontWeight:600, color:'#ef4444' }}>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ══════════════ MAIN CONTENT ══════════════════════════════════ */}
      <div
        style={{
          paddingTop: SB_GAP + TB_H + SB_GAP, // gap + topbar + gap below topbar
          marginLeft: contentMarginLeft,
          transition:'margin-left 0.28s cubic-bezier(0.4,0,0.2,1)',
          minHeight:'100vh',
        }}
      >
        <main style={{ padding:24 }}>
          {isCurrentPathEnabled() ? children : renderDisabled()}
        </main>
      </div>

      <SpotlightSearch open={spotOpen} setOpen={setSpotOpen}/>
    </div>
  );
};
